/**
 * The router — which intent a question is, with which entities.
 *
 * Replaces the regex engine's twenty-four first-match-wins branches with a
 * scored classification over the {@link INTENTS} registry:
 *
 * 1. Extract entities from the question (`./entities.ts`).
 * 2. For each intent, fill any entity it **needs** and the question lacks —
 *    from the page (`./page-context.ts`) or the conversation — and score it.
 *    A fill without a deictic word ("this machine", "it") costs a point, so
 *    a question that is complete on its own is preferred.
 * 3. A follow-up ("and at Kalbarri?", "why?", "what would that cost?")
 *    re-runs or redirects the last turn's intent (`./conversation.ts`).
 * 4. The best score above {@link THRESHOLD} wins, ties broken by the intent's
 *    fixed priority. Nothing above it → the fallback, which offers the three
 *    nearest intents' example prompts.
 *
 * Deterministic: same question, page and memory in → same classification.
 *
 * Engine layer — pure.
 */

import type { ConversationMemory } from './answer-types.ts';
import {
  detectFollowUp,
  mergeEntities,
  type EngineMemory,
  type FollowUp,
} from './conversation.ts';
import { alertFor, extractEntities, type Entities } from './entities.ts';
import { INTENTS, INTENT_BY_ID } from './intents/index.ts';
import type { IntentDef, ScoreCtx } from './intents/types.ts';
import { pageContext, type PageContext } from './page-context.ts';

/** Below this, no intent owns the question. */
export const THRESHOLD = 1.5;
/** What filling a needed entity from page/memory without a deictic word costs. */
const IMPLICIT_FILL_PENALTY = 1;

export interface ScoredIntent {
  readonly id: string;
  readonly score: number;
}

export interface Classification {
  /** `null` → the fallback. */
  readonly intent: IntentDef | null;
  readonly score: number;
  /** Entities resolved for the winner (page / memory fills applied). */
  readonly entities: Entities;
  /** The question the intent answers — the remembered one on a swap. */
  readonly q: string;
  readonly page: PageContext;
  readonly followUp?: FollowUp['kind'];
  /** Best other candidates, for the fallback and for debugging. */
  readonly runnersUp: readonly ScoredIntent[];
}

export interface ClassifyOptions {
  readonly pathname: string;
  readonly memory?: ConversationMemory;
}

/** Fill the entities an intent needs. `null` = cannot be satisfied. */
function resolveFor(
  intent: IntentDef,
  base: Entities,
  page: PageContext,
  memory: EngineMemory | undefined,
): { E: Entities; implicit: boolean } | null {
  const E: Entities = { ...base };
  let implicit = false;
  const d = base.deictic;

  // A deictic reference fills from context for any intent.
  if (!E.machine && (d.thisMachine || d.it)) {
    const fromPage = page.machine;
    const fromMemory = memory?.machine;
    const pick = d.it ? (fromMemory ?? fromPage) : (fromPage ?? fromMemory);
    if (pick) {
      E.machine = pick;
      E.machineFrom = pick === fromPage && (d.thisMachine || !fromMemory) ? 'page' : 'memory';
    }
  }
  if (!E.mine && d.thisSite && page.mine) {
    E.mine = page.mine;
    E.mineFrom = 'page';
  }
  if (!E.module && d.thisModule && page.module) {
    E.module = page.module;
    E.moduleFrom = 'page';
  }

  for (const need of intent.needs ?? []) {
    if (need === 'machine' && !E.machine) {
      const pick = page.machine ?? memory?.machine;
      if (!pick) return null;
      E.machine = pick;
      E.machineFrom = pick === page.machine ? 'page' : 'memory';
      implicit = true;
    }
    if (need === 'module' && !E.module) {
      const pick = page.module ?? (memory?.asked?.module as Entities['module']);
      if (!pick) return null;
      E.module = pick;
      E.moduleFrom = pick === page.module ? 'page' : 'memory';
      implicit = !d.thisModule;
    }
    if (need === 'mine' && !E.mine) {
      const pick = page.mine ?? memory?.mine;
      if (!pick) return null;
      E.mine = pick;
      E.mineFrom = pick === page.mine ? 'page' : 'memory';
      implicit = true;
    }
    if (need === 'report' && !page.report && !memory?.report) return null;
  }
  return { E, implicit };
}

/** Score every intent against a question. Exported for the regression gate. */
export function scoreAll(
  q: string,
  opts: ClassifyOptions,
): { scored: { intent: IntentDef; score: number; E: Entities }[]; base: Entities; page: PageContext } {
  const L = q.toLowerCase();
  const page = pageContext(opts.pathname);
  const memory = opts.memory as EngineMemory | undefined;
  const base = extractEntities(q);
  const sctx: ScoreCtx = { q, L, page, memory };
  const scored = INTENTS.map((intent) => {
    const r = resolveFor(intent, base, page, memory);
    if (!r) return { intent, score: 0, E: base };
    let score = intent.score(L, r.E, sctx);
    if (score > 0 && r.implicit) score = Math.max(0, score - IMPLICIT_FILL_PENALTY);
    return { intent, score, E: r.E };
  }).sort((a, b) => b.score - a.score || b.intent.priority - a.intent.priority);
  return { scored, base, page };
}

/** Which intent answers `q`, asked from `pathname` after `memory`. */
export function classify(q: string, opts: ClassifyOptions): Classification {
  const question = q.trim();
  const L = question.toLowerCase();
  const memory = opts.memory as EngineMemory | undefined;
  const { scored, base, page } = scoreAll(question, opts);
  const best = scored[0];
  const runnersUp = scored.slice(1, 6).map((s) => ({ id: s.intent.id, score: s.score }));

  const fu = detectFollowUp(L, base, memory, best?.score ?? 0);
  if (fu && memory?.intent) {
    if (fu.kind === 'swap') {
      const swap = swapTarget(base, memory);
      if (swap) return { ...swap, score: best?.score ?? 0, page, followUp: 'swap', runnersUp };
    } else {
      const redirected = followUpTarget(fu.kind, memory);
      if (redirected) {
        return {
          intent: redirected,
          score: best?.score ?? 0,
          entities: followUpEntities(base, memory),
          q: question,
          page,
          followUp: fu.kind,
          runnersUp,
        };
      }
    }
  }

  if (!best || best.score < THRESHOLD) {
    return { intent: null, score: best?.score ?? 0, entities: base, q: question, page, runnersUp: scored.slice(0, 6).map((s) => ({ id: s.intent.id, score: s.score })) };
  }
  return { intent: best.intent, score: best.score, entities: best.E, q: question, page, runnersUp };
}

/** Does `E` carry everything `intent` needs? */
const satisfied = (intent: IntentDef, E: Entities): boolean =>
  (intent.needs ?? []).every((n) =>
    n === 'machine' ? !!E.machine : n === 'module' ? !!E.module : n === 'mine' ? !!E.mine : true,
  );

/**
 * A swap re-runs the last intent on the last question with the new entity —
 * or, when the last intent cannot take it ("and the loaders?" after a job
 * cost on one machine), the last question asked in its own right.
 */
function swapTarget(
  base: Entities,
  memory: EngineMemory,
): { intent: IntentDef; entities: Entities; q: string } | null {
  const last = memory.intent ? INTENT_BY_ID[memory.intent] : undefined;
  if (last && memory.q) {
    const E = mergeEntities(base, memory.asked);
    if (satisfied(last, E)) return { intent: last, entities: E, q: memory.q };
  }
  const root = memory.root ? INTENT_BY_ID[memory.root.intent] : undefined;
  if (root && memory.root) {
    const E = mergeEntities(base, memory.root.asked);
    if (satisfied(root, E)) return { intent: root, entities: E, q: memory.root.q };
  }
  return null;
}

/** The intent a "why?" / "what would that cost?" runs. */
function followUpTarget(kind: FollowUp['kind'], memory: EngineMemory): IntentDef | null {
  const last = memory.intent ? INTENT_BY_ID[memory.intent] : undefined;
  const machine = memory.machine;
  if (kind === 'cost') {
    if (machine) return INTENT_BY_ID.job_cost;
    return INTENT_BY_ID.cost_of_inaction;
  }
  // why
  if (!machine) return last?.family === 'cost' ? (last ?? null) : null;
  if (['incident_cost', 'why_failed'].includes(memory.intent ?? '')) return INTENT_BY_ID.why_failed;
  if (['risk_ranking', 'prediction_confidence', 'report_focus', 'hotspots', 'cost_of_inaction'].includes(memory.intent ?? '')) {
    return alertFor(machine) ? INTENT_BY_ID.prediction_confidence : INTENT_BY_ID.risk_ranking;
  }
  return INTENT_BY_ID.machine_history;
}

/** "why?" / "what would that cost?" — the remembered machine and component. */
function followUpEntities(base: Entities, memory: EngineMemory): Entities {
  const comp = memory.component;
  const E: Entities = {
    ...base,
    machine: base.machine ?? memory.machine,
    machineFrom: base.machine ? 'text' : memory.machine ? 'memory' : undefined,
    mine: base.mine ?? memory.mine,
    mineFrom: base.mine ? 'text' : memory.mine ? 'memory' : undefined,
  };
  if (!base.components.length && comp) {
    return { ...E, components: extractEntities(comp).components };
  }
  return E;
}
